import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import posthog from 'posthog-js'
import {
  supabaseGestionPension as supabase
} from '../../../../config/settings/supabase-client'
import {
  onLoadUserAndProfile,
  onSignOut
} from './AuthProvider.slice'
import { getLogger } from '../../../../core/debuggers'

const logger = getLogger('SessionWatcher')

const SessionWatcher = () => {
  const dispatch = useDispatch()

  const lastUserId = useRef(null)
  const logoutHandled = useRef(false)

  useEffect(() => {
    logger.debug('🚀 Init SessionWatcher')

    const loadProfileOnce = (session, { captureLogin = false } = {}) => {
      const user = session?.user
      if (!user?.id) {
        return
      }

      // SIGNED_IN + INITIAL_SESSION often fire together; only sync once per user.
      if (lastUserId.current === user.id) {
        return
      }

      lastUserId.current = user.id
      logoutHandled.current = false
      posthog.identify(user.id, {
        email: user.email,
        name: user.user_metadata?.full_name,
      })
      if (captureLogin) {
        posthog.capture('login_completed')
      }
      dispatch(onLoadUserAndProfile())
    }

    const { data: listener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.debug('🔔 onAuthStateChange', event)

        switch (event) {
          case 'INITIAL_SESSION': {
            if (session?.user) {
              loadProfileOnce(session)
            } else {
              // Logged-out boot: still resolve auth so protected routes can redirect to login.
              dispatch(onLoadUserAndProfile())
            }
            break
          }

          case 'SIGNED_IN': {
            loadProfileOnce(session, { captureLogin: true })
            break
          }

          case 'USER_UPDATED': {
            dispatch(onLoadUserAndProfile())
            break
          }

          case 'TOKEN_REFRESHED': {
            // ❌ nunca tocar estado aquí
            break
          }

          case 'SIGNED_OUT': {
            // 🛑 FIX CLAVE
            if (logoutHandled.current) {
              logger.debug('🔁 SIGNED_OUT ignored (already handled)')
              return
            }

            logger.debug('👋 SIGNED_OUT handled')
            logoutHandled.current = true
            lastUserId.current = null

            posthog.reset()
            dispatch(onSignOut())
            break
          }

          default:
            logger.debug('ℹ️ Unhandled auth event:', event)
        }
      }
    )

    return () => {
      logger.debug('🧹 SessionWatcher cleanup')
      listener.subscription.unsubscribe()
    }
  }, [dispatch])

  return null
}

export default SessionWatcher
